import {
  AfterViewInit,
  Component,
  ViewChild,
  Input,
  Output,
  EventEmitter,
  OnDestroy,
  ChangeDetectionStrategy,
  ChangeDetectorRef
} from '@angular/core';
import { MatPaginator, MatPaginatorIntl, MatPaginatorModule } from '@angular/material/paginator';
import { MatTableDataSource, MatTableModule } from '@angular/material/table';
import { CommonModule } from '@angular/common';
import { MatInputModule } from '@angular/material/input';
import { MatSortModule, MatSort } from '@angular/material/sort';
import { MatIconModule } from '@angular/material/icon';
import { MatButtonModule } from '@angular/material/button';
import { MatCheckboxModule } from '@angular/material/checkbox';
import { MatTooltipModule } from '@angular/material/tooltip';
import { FormControl, ReactiveFormsModule } from '@angular/forms';
import { environment } from 'src/environments/environment';
import {
  Observable,
  Subject,
  BehaviorSubject,
  combineLatest,
  debounceTime,
  distinctUntilChanged,
  takeUntil,
  switchMap,
  map,
  startWith,
  tap,
  of
} from 'rxjs';
import { Pagination } from 'src/app/core/services/pagination.service';
import { PagedResultDTO } from '@models/shared';

@Component({
    selector: 'app-data-table-optimized',
    imports: [
        CommonModule,
        MatPaginatorModule,
        MatTableModule,
        MatInputModule,
        MatSortModule,
        MatIconModule,
        MatButtonModule,
        MatCheckboxModule,
        MatTooltipModule,
        ReactiveFormsModule
    ],
    templateUrl: './data-table-optimized.component.html',
    styleUrls: ['./data-table-optimized.component.scss'],
    providers: [{ provide: MatPaginatorIntl, useClass: Pagination }],
    changeDetection: ChangeDetectionStrategy.OnPush
})
export class DataTableOptimizedComponent<T = any> implements AfterViewInit, OnDestroy {
  @Input() displayedColumns: string[] = [];
  @Input() getData!: (pageIndex: number, pageSize: number, filter?: any) => Observable<PagedResultDTO<any>>;
  @Input() externalFilter$: Observable<any> = of(null);
  @Input() cellStyleCss: any = {};
  @Input() headerStyleCss: any = {};
  @Input() selectionKey: string = 'liste';
  @Input() hasView: boolean = false;
  @Input() validable: boolean = false;

  @Input() refusable: boolean = false;
  @Input() advicable: boolean = false;
  @Input() selectable: boolean = false;
  @Input() editable: boolean = false;
  @Input() renewable: boolean = false;
  @Input() deletable: boolean = false;
  @Input() recallRefusable: boolean = false;
  @Input() downloadable: boolean = false;
  @Input() downloadablePJ: boolean = false;
  @Input() downloadablePJ_hidden: boolean = false;
  @Input() filter: boolean = false;
  @Input() hasPagination: boolean = true;
  @Input() limit: number = environment.pagination.limit;
  @Input() offset: number = environment.pagination.offset;

  @Output() validateSingle = new EventEmitter<T>();
  @Output() refuseSingle = new EventEmitter<T>();
  @Output() showDetail = new EventEmitter<T>();
  @Output() selectionChange = new EventEmitter<T[]>();
  @Output() validateItem = new EventEmitter<T>();
  @Output() refuseItem = new EventEmitter<T>();
  @Output() validateMultiple = new EventEmitter<T[]>();
  @Output() refuseMultiple = new EventEmitter<T[]>();
  @Output() editItemEvent = new EventEmitter<T>();
  @Output() renewItemEvent = new EventEmitter<T>();
  @Output() deleteItemEvent = new EventEmitter<T>();
  @Output() downloadItemEvent = new EventEmitter<T>();
  @Output() downloadPJEvent = new EventEmitter<T>();
  @Output() getAdviceEvent = new EventEmitter<T>();
  @Output() recallRefuseItem = new EventEmitter<T>();

  dataSource = new MatTableDataSource<T>([]);
  filterControl = new FormControl('');
  length = 0;
  @ViewChild(MatSort) sort!: MatSort;
  @ViewChild(MatPaginator) paginator!: MatPaginator;
  @ViewChild('filterInput') filterInput: any;

  private readonly destroy$ = new Subject<void>();
  private readonly refresh$ = new Subject<void>();
  private readonly selectedItems$ = new BehaviorSubject<Map<string, T>>(new Map());
  private allData: T[] = [];

  get effectiveDisplayedColumns(): string[] {
    return this.selectable ? ['Sélectionner', ...this.displayedColumns] : this.displayedColumns;
  }

  constructor(private readonly cdr: ChangeDetectorRef) {
    // Emit selection changes
    this.selectedItems$.pipe(takeUntil(this.destroy$)).subscribe((selections) => {
      this.selectionChange.emit(Array.from(selections.values()));
      this.cdr.markForCheck();
    });
  }

  private filterData(data: T[], filterValue: string | null): T[] {
    if (!filterValue) return data;
    return data.filter((item) =>
      Object.values(item as any).some((val) => val?.toString()?.toLowerCase().includes(filterValue.toLowerCase()))
    );
  }

  private sortData(data: T[], sort: { active: string; direction: string }): T[] {
    if (!sort.active || !sort.direction) return data;
    return [...data].sort((a, b) => {
      const isAsc = sort.direction === 'asc';
      const valueA = (a as any)[sort.active];
      const valueB = (b as any)[sort.active];
      return (valueA < valueB ? -1 : 1) * (isAsc ? 1 : -1);
    });
  }

  ngAfterViewInit() {
    combineLatest([
      this.refresh$.pipe(startWith(undefined)),
      this.filterControl.valueChanges.pipe(
        startWith(''),
        debounceTime(300),
        distinctUntilChanged(),
        tap(() => (this.paginator.pageIndex = 0))
      ),
      this.externalFilter$.pipe(
        startWith(null),
        distinctUntilChanged(),
        tap(() => (this.paginator.pageIndex = 0))
      ),
      this.paginator.page.pipe(
        startWith({
          pageIndex: this.offset,
          pageSize: this.limit,
          length: 0
        })
      ),
      this.sort.sortChange.pipe(
        startWith({ active: '', direction: '' }),
        tap(() => (this.paginator.pageIndex = 0))
      )
    ])
      .pipe(
        takeUntil(this.destroy$),
        switchMap(([_, filterValue, externalFilter, page, sort]) => {
          return this.getData(page.pageIndex, page.pageSize, externalFilter).pipe(
            map((result) => {
              this.allData = result.results;
              const filteredData = this.filterData(result.results, filterValue);
              const sortedData = this.sortData(filteredData, sort);
              return {
                data: sortedData,
                length: result.totalRecords
              };
            })
          );
        })
      )
      .subscribe(({ data, length }) => {
        this.dataSource.data = data;
        this.length = length;
        this.paginator.length = length;
        this.cdr.detectChanges();
      });
  }

  ngOnDestroy() {
    this.destroy$.next();
    this.destroy$.complete();
    this.selectedItems$.complete();
  }

  refresh() {
    this.refresh$.next();
  }

  // Selection methods
  isSelected(row: T): boolean {
    return this.selectedItems$.value.has(this.getItemId(row));
  }

  isAllSelected(): boolean {
    return this.allData.length > 0 && this.allData.every((item) => this.selectedItems$.value.has(this.getItemId(item)));
  }

  toggleAllRows() {
    if (this.isAllSelected()) {
      this.clearSelection();
    } else {
      const newSelection = new Map(this.selectedItems$.value);
      this.allData.forEach((item) => {
        newSelection.set(this.getItemId(item), item);
      });
      this.selectedItems$.next(newSelection);
    }
  }

  toggleSelection(row: T) {
    const newSelection = new Map(this.selectedItems$.value);
    const id = this.getItemId(row);

    if (newSelection.has(id)) {
      newSelection.delete(id);
    } else {
      newSelection.set(id, row);
    }

    this.selectedItems$.next(newSelection);
  }

  clearSelection() {
    this.selectedItems$.next(new Map());
  }

  getSelectedItems(): T[] {
    return Array.from(this.selectedItems$.value.values());
  }

  private getItemId(item: T): string {
    return (item as any)?.id?.toString() || JSON.stringify(item);
  }

  checkboxLabel(row?: T): string {
    if (!row) {
      return `${this.isAllSelected() ? 'deselect' : 'select'} all`;
    }
    return `${this.isSelected(row) ? 'deselect' : 'select'} row`;
  }

  // Action methods
  validateSingleItem(element: T) {
    this.validateItem.emit(element);
  }

  refuseSingleItem(element: T) {
    this.refuseItem.emit(element);
  }

  showTableDetail(element: T) {
    this.showDetail.emit(element);
  }

  validateMultipleItems() {
    const selectedItems = this.getSelectedItems();
    this.validateMultiple.emit(selectedItems);
  }

  refuseMultipleItems() {
    const selectedItems = this.getSelectedItems();
    this.refuseMultiple.emit(selectedItems);
  }

  editSingleItem(element: T) {
    this.editItemEvent.emit(element);
  }
  renewItem(element: T) {
    this.renewItemEvent.emit(element);
  }

  deleteSingleItem(element: T) {
    this.deleteItemEvent.emit(element);
  }

  downloadSingleItem(element: T) {
    this.downloadItemEvent.emit(element);
  }

  downloadPJ(element: T) {
    this.downloadPJEvent.emit(element);
  }

  getAdvice(element: T) {
    this.getAdviceEvent.emit(element);
  }

  recallRefuse(element: T) {
    this.recallRefuseItem.emit(element);
  }

  isObjectType(value: any): boolean {
    return typeof value === 'object' && value !== null && !(value instanceof Date);
  }
}
